<template>
  <ModalStructure
    class="groupsModal"
    :shouldCloseOnBlur="false"
    :showModal="showModal"
    @close="handleClose"
  >
    <template v-slot:header>
      <HeadingMedium
        class="groupsModalHeader"
      >
        Enroll By User Group
      </HeadingMedium>
    </template>
    <template v-slot:body>
      <UserGroupItemTable
        ref="itemTable"
        :boundItemsUrl="boundItemsUrl"
        class="applicationUserGroups"
        :showType="false"
        @rowSelected="handleRowSelected"
      />
    </template>
    <template v-slot:footer>
      <div
        class="groupsModalFooter"
      >
        <ButtonGroupSave
          :isFullWidth="false"
          :primaryButtonProps="primaryButtonProps"
          :secondaryButtonProps="secondaryButtonProps"
          @primaryAction="handleOnSave"
          @secondaryAction="handleClose"
        />
      </div>
    </template>
  </ModalStructure>
</template>
<script>

import {
  ButtonGroupSave,
  HeadingMedium,
  ModalStructure,
  Types,
} from '@jumpcloud/ui-components';
import { StoreModelMap } from 'client/util/Constants';
import { implementationChecklistTasks } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { trialPropsData } from 'client/util/Constants/FeatureTrialConstants';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import ItemTypeConstants from 'client/util/Constants/ItemTypeConstants';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';

const { PrimaryButtonProps, SecondaryButtonProps } = Types;

const { ItemTypeConsts } = ItemTypeConstants;
const { application, userGroup } = ItemTypeConsts;
const applicationModel = StoreModelMap[application];
const onboardingModule = 'OnboardingModule';
const PasswordManagerUserCollection = 'PasswordManagerUserCollection';
const passwordManagerFeatureCode = ItemTypeConsts.passwordManagement;

export default {
  name: 'PasswordManagerUserGroupModal',

  components: {
    ButtonGroupSave,
    HeadingMedium,
    ModalStructure,
    UserGroupItemTable,
  },

  mixins: [FeatureTrialsMixin],

  props: {
    applicationId: {
      default: '',
      type: String,
    },
    showModal: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      boundItemsUrl: `/api/v2/applications/${this.applicationId}/usergroups`,
      enrollingUsers: false,
      checkBoxCount: 0,
    };
  },

  computed: {
    ...mapGetters(applicationModel, {
      haveAssociationsChanged: 'haveAssociationsChanged',
    }),

    primaryButtonProps() {
      return PrimaryButtonProps({
        isDisabled: !this.haveAssociationsChanged([userGroup]) || this.enrollingUsers,
        text: 'save',
      });
    },

    secondaryButtonProps() {
      return SecondaryButtonProps({
        isDisabled: this.enrollingUsers,
        hasNoBackground: false,
        hasOutline: true,
        text: 'cancel',
        type: 'secondary',
      });
    },
  },

  watch: {
    showModal: {
      handler(newValue) {
        if (newValue === true) {
          this.setItemField({ key: 'id', value: this.applicationId });
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(onboardingModule, [
      'updateImplementationTaskStatusAuto',
    ]),

    ...mapActions(PasswordManagerUserCollection, {
      updateUsersRefreshKey: 'updateRefreshKey',
    }),

    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(applicationModel, [
      'bindApplicationToUserGroups',
      'setItemField',
      'resetItem',
    ]),

    handleClose() {
      this.refreshStore();
      this.$emit('close');
    },

    async handleOnSave() {
      try {
        this.enrollingUsers = true;
        await this.bindApplicationToUserGroups();
        this.$emit('close');
        this.updateUsersRefreshKey();
        this.triggerSuccessNotification({ title: 'Saved', message: 'Changes have been saved.' });
        this.updateImplementationTaskStatusAuto({
          taskType: implementationChecklistTasks.enrollUserViaUserGroups,
        });
        this.refreshTrialData(passwordManagerFeatureCode);
      } catch (error) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'There was a problem saving your changes. Please try again.',
        });
      } finally {
        this.enrollingUsers = false;
      }
    },

    handleRowSelected(selectedCount) {
      this.checkBoxCount = selectedCount;
    },

    refreshStore() {
      this.resetItem();
    },

    showAddOnTrialModal() {
      ModalEventBus.open({
        component: 'TrialModal',
        componentProps: trialPropsData.passwordManagement,
        isLargeModal: true,
      });
    },
  },
};
</script>

<style scoped>
.applicationUserGroups {
  height: calc(100vh - 20rem);
  margin-top: 20px;
  overflow-x: auto;
}

.groupsModal {
  --modal-width: 800px;
}

.groupsModal.groupsModal >>> > div {
  background-color: var(--jcWhite);
}

.groupsModalFooter {
  padding: 0 40px 0 20px;
  width: 100%;
}

.groupsModalHeader {
  padding: 0 20px;
}
</style>
